import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Link, LinkProps } from 'react-router-dom';
import { Button, Loader } from 'ncoded-component-library';
import { NotificationType } from 'models/Notification';
import { AvatarWithInitials } from 'components/AvatarWithInitials';
import XIcon from 'icons/X.icon';
import usePastTime from 'hooks/usePastTime';
import { SIZE } from 'models/Size';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import useAction, {
  NotificationAction,
} from 'providers/Notifications/hooks/useAction';
import { BUSINESS_ROUTE, NEW_WINDOW_PROPS } from 'data/global';

import './NotificationCard.styles.scss';

export type NotificationCardProps = {
  className?: string;
  loading?: boolean;
  notification?: NotificationType;
  ctaPrimary: string;
  ctaSecondary: string;
  ctaText: string;
  ctaIcon: ReactNode;
  contentText: string;
  onDynamicTo?: () => Promise<string>;
  onRead?: (notifId: string, keepDrawerOpen?: boolean) => void;
  onUnread?: (notifId: string, keepDrawerOpen?: boolean) => void;
  onDelete?: (notif: NotificationType) => void;
} & Partial<LinkProps>;

const CREAM_NOTIFICATIONS = [53];

const NotificationCard: React.FC<NotificationCardProps> = (props) => {
  const {
    className,
    loading,
    notification,
    ctaPrimary,
    ctaSecondary,
    ctaText,
    ctaIcon,
    contentText,
    to = BUSINESS_ROUTE,
    onDynamicTo,
    onDelete,
    onRead,
    onUnread,
    ...rest
  } = props;

  const [isLoadingDynamicUrl, setIsLoadingDynamicUrl] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { popServerError } = useContext(PopNotificationsContext);

  const {
    id: notifId,
    isRead,
    createdAt,
    typeCode,
    content: { sender },
  } = notification || {};

  const isCreamy = CREAM_NOTIFICATIONS.includes(typeCode);

  const classes = classNames(
    'notification-card',
    {
      'notification-card--cream': isCreamy,
    },
    className,
  );

  const isExternalTo = useMemo(() => `${to}`.startsWith('http'), [to]);
  const isExternalUrl = useMemo(
    () => onDynamicTo || isExternalTo,
    [isExternalTo, onDynamicTo],
  );

  const pastTime = usePastTime();

  const handleClickExternal = useCallback(
    async (e) => {
      e.preventDefault();

      if (isLoadingDynamicUrl || !isExternalUrl) {
        return;
      }

      try {
        setIsLoadingDynamicUrl(true);

        const externalUrl = isExternalTo ? `${to}` : await onDynamicTo();

        await onRead(notifId, true);

        window.location.href = externalUrl;
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoadingDynamicUrl(false);
      }
    },
    [
      isExternalTo,
      isExternalUrl,
      isLoadingDynamicUrl,
      notifId,
      onDynamicTo,
      onRead,
      popServerError,
      to,
    ],
  );

  const { getActionByType } = useAction();

  const primaryAction = useMemo(
    () => getActionByType(notification),
    [getActionByType, notification],
  );

  const secondaryAction = useMemo(
    () => getActionByType(notification, 'secondary'),
    [getActionByType, notification],
  );

  const hasAction = useMemo(
    () => primaryAction || secondaryAction,
    [primaryAction, secondaryAction],
  );

  const handleAction = useCallback(
    async (
      notification: NotificationType,
      action: (config: NotificationAction) => void,
    ) => {
      try {
        setIsSubmitting(true);
        onRead(notification._id);
        action({ notification });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsSubmitting(false);
      }
    },
    [onRead, popServerError],
  );

  return (
    <div className={classes}>
      {loading && <Loader />}

      <div className="notification-card__avatar">
        <AvatarWithInitials user={sender} size={SIZE.BIG} />

        <span
          className={classNames('notification-card__avatar__dot', 'clickable', {
            empty: isRead,
          })}
          onClick={() => {
            if (loading) return;

            isRead ? onUnread(notifId, true) : onRead(notifId, true);
          }}
        />
      </div>

      <div className="notification-card__content">
        <div className="notification-card__content__heading">
          <p className="notification-card__content__heading__text">
            {contentText}
          </p>
          <Button
            variant="icon"
            aria-label="Delete"
            icon={XIcon}
            onClick={() => onDelete(notification)}
          />
        </div>

        <div className="notification-card__content__actions">
          <p className="notification-card__content__time">
            {pastTime(createdAt)}
          </p>

          {hasAction ? (
            <div className="buttons">
              {primaryAction && (
                <Button
                  disabled={isSubmitting}
                  onClick={() => handleAction(notification, primaryAction)}
                >
                  {ctaPrimary}
                  {ctaIcon}
                </Button>
              )}
              {secondaryAction && (
                <Button
                  variant="outline"
                  disabled={isSubmitting}
                  onClick={() => handleAction(notification, secondaryAction)}
                >
                  {ctaSecondary}
                  {ctaIcon}
                </Button>
              )}
            </div>
          ) : isExternalUrl ? (
            <a
              href={to as string}
              className={classNames('notification-card__proceed', {
                'notification-card__proceed__disabled': isLoadingDynamicUrl,
              })}
              onClick={handleClickExternal}
              {...NEW_WINDOW_PROPS}
            >
              {ctaText}
              {ctaIcon}
              {isLoadingDynamicUrl && <Loader size="small" />}
            </a>
          ) : (
            <Link
              {...rest}
              className="notification-card__proceed"
              to={to}
              onClick={() => onRead(notifId)}
            >
              {ctaText}
              {ctaIcon}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(NotificationCard);
